'use client'

import React, { useState } from 'react'
import type { AnnualReportIntro } from '@/.generated/sanityTypes/graphql'
import { useLocale } from 'next-intl'
import type { Locale } from '@/i18n'
import { withNewLines } from '@/lib/newline'
import { cx } from '@monorepo/helper/cx'
import { getTheme } from '@/lib/theme'
import Image from '@/components/Image'
import { usePathname } from 'next/navigation'

const headingSizeClassName = {
  sm: 'text-4xl md:text-5xl xl:text-6xl',
  md: 'text-4xl md:text-7xl xl:text-8xl',
  lg: 'text-6xl md:text-8xl xl:text-10xl',
}

const subHeadingSizeClassName = {
  sm: 'text-lg md:text-xl xl:text-2xl',
  md: 'text-xl md:text-2xl xl:text-2xl',
  lg: 'text-2xl md:text-4xl xl:text-[40px]',
}

const IntroBlock = ({
  title,
  subtitle,
  image,
  halfWidth,
  headingSize = 'md',
  colorlist,
}: AnnualReportIntro) => {
  const locale = useLocale() as Locale
  const theme = getTheme(colorlist)
  const pathname = usePathname()
  const [is2024Home] = useState(
    pathname?.includes('2024') && pathname?.split('/').length === 3,
  )

  return (
    <div
      className={cx(
        'relative flex h-[calc(100vh-65px)] max-h-[960px] overflow-hidden',
        theme.bg,
      )}
    >
      <div
        className={cx(
          'text-red z-10 flex w-full flex-col justify-end gap-8 px-8 py-12 font-medium md:w-[85%] md:justify-start md:gap-14 xl:p-16',
          theme.headline,
          is2024Home && '!text-blue',
        )}
      >
        <h1
          className={cx(
            'hyphens-auto lg:hyphens-none',
            headingSizeClassName?.[
              headingSize as keyof typeof headingSizeClassName
            ] || headingSizeClassName.md,
          )}
        >
          {withNewLines(title?.[locale])}
        </h1>
        <p
          className={cx(
            'hyphens-auto md:hyphens-none',
            subHeadingSizeClassName?.[
              headingSize as keyof typeof subHeadingSizeClassName
            ] || subHeadingSizeClassName.md,
          )}
        >
          {withNewLines(subtitle?.[locale])}
        </p>
      </div>
      <div className="absolute left-0 flex h-full w-full justify-end">
        <div className={cx('relative', halfWidth ? 'w-3/4' : 'w-full')}>
          {image?.asset && (
            // eslint-disable-next-line jsx-a11y/alt-text
            <Image image={image} fill cover />
          )}
        </div>
      </div>
    </div>
  )
}

export default IntroBlock
